import { WarningAmberOutlined } from '@mui/icons-material'
import { Button, Typography } from '@mui/material'
import { RJSFValidationError } from '@rjsf/utils'
import { WorkingStepIconSvg } from 'components/Common/WorkingSteps/WorkingStepIcon'
import { useLocalizedWorkingStepLabel } from 'components/Localization/useLocalizedWorkingStepLabel'
import { WorkingStepResourceSelector } from 'features/BillOfMaterials/WorkingSteps/WorkingStepResourceSelector'
import WorkingStepAutoComplete from 'features/WorkingSteps/WorkingStepAutoComplete'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import { WorkingStepType } from '../../../../services/APIs/InternalAPI/internal-api.contracts'

type Validations = Partial<Record<WorkingStepType, RJSFValidationError[]>>

type WorkingStepSelectorProps = {
  affectedBomItemPointers: Array<BomItemPointer>
  bomItemPointer: BomItemPointer
  currentWorkingSteps: WorkingStepType[]
  availableWorkingSteps: WorkingStepType[]
  activeWorkingStep: WorkingStepType | null
  onActiveWorkingStepChange: (workingStepType: WorkingStepType) => void
  onWorkingStepAdded: (workingStepType: WorkingStepType) => void
  onWorkingStepRemoved: (workingStepType: WorkingStepType) => void
  markDirty: (onSave: () => void | Promise<void>, actionName: string) => void
  handleSave: (actionName: string) => void | Promise<void>
  validations: Validations | null
  disableResourceSelectors?: boolean
}

export const WorkingStepSelectorPanel = ({
  currentWorkingSteps,
  availableWorkingSteps,
  activeWorkingStep,
  onActiveWorkingStepChange,
  onWorkingStepAdded,
  affectedBomItemPointers,
  onWorkingStepRemoved,
  validations,
  disableResourceSelectors,
}: WorkingStepSelectorProps) => {
  const getLocalizedWorkingStep = useLocalizedWorkingStepLabel()

  return (
    <div className={'ws__list__root'}>
      <div className={'ws__search'}>
        <WorkingStepAutoComplete
          availableWorkingSteps={availableWorkingSteps}
          onOptionSelected={onWorkingStepAdded}
          selectedOption={null}
          placeholder="available working steps"
          AutoCompleteProps={{
            disableCloseOnSelect: true,
          }}
        />
      </div>

      <div className="ws__list__container">
        <div className={'ws__list__title'}>
          <Typography variant="caption" fontWeight={'bold'}>
            included working steps
          </Typography>
        </div>
        <ul className={'ws__list'}>
          {currentWorkingSteps.map((ws) => {
            return (
              <li
                key={ws}
                className={`ws__item ${activeWorkingStep === ws ? 'ws__active' : ''}`}
                onClick={() => {
                  onActiveWorkingStepChange(ws)
                }}
              >
                <div className={`ws__item__description`}>
                  <WorkingStepIconSvg
                    workingStepType={ws}
                    attributes={{ width: '32px', height: '32px' }}
                    disableTooltip
                  />
                  <div>
                    <Typography variant="body2" fontWeight="bold">
                      {getLocalizedWorkingStep(ws)}
                    </Typography>
                    <div>
                      <WorkingStepResourceSelector
                        bomItemPointers={affectedBomItemPointers}
                        workingStepType={ws}
                        useStoreWorkingStep
                        enableClickPropagation
                        withLabel
                        disabled={disableResourceSelectors}
                      />
                    </div>
                  </div>
                </div>
                <div className="ws__item__actions">
                  {validations?.[ws]?.length ? (
                    <span>
                      <WarningAmberOutlined fontSize="small" color="warning" />
                      <Typography variant="caption" color="warning">
                        {validations[ws]?.length} issues
                      </Typography>
                    </span>
                  ) : null}
                  <Button
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation()
                      onWorkingStepRemoved(ws)
                    }}
                    data-testid="bom-details-remove-working-step"
                  >
                    remove
                  </Button>
                </div>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}
